import moment from "moment";
import { formatDate, toUtcDay, formatUTCDate, getPreviousDayForGivenDate, getNextDay, formatDecimalPlace, formatTodaysDate, formatNumber, formatQuantity } from "common/formatters";
import TypedSelect from "common/ui/TypedSelect";
import Commodity from "domain/commodity";
import { DeliveryMethod } from "domain/deliveryMethod";
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import Offer from "domain/offer";
import { getTasFallbackOptionLabel, AllTasFallbackOptions } from "domain/tasFallbackOption";
import PhysicalLocation from "domain/physicalLocation";
import OfferType, { OfferTypeEnum } from "domain/offerType";
import React, { useState } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip, Collapse, InputGroup, Alert } from 'react-bootstrap';
import { Trader } from "features/settings/traders/trader";
import NumberEditor from "common/ui/NumberEditor";
import { PaymentOption } from "domain/paymentOption";
import * as NP from "number-precision";
import { AllOrderTypes, AllOrderTypesWithManualEntry, FlatCashOrderTypes, getOfferContractOrderTypeName, OrderType } from "domain/orderType";
import FuturesContract from "domain/futuresContract";
import CustomerAccountPicker from "features/settings/producers/CustomerAccountPicker";
import CountdownTimer from "common/ui/CountdownTimer";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { TimerResult } from "react-timer-hook";
import Contract from "domain/contract";
import { getPriceLabelForOffer } from "common/formatters/strings";
import { useCustomerWillSellText } from "common/formatters/useCustomerWillSellText";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { isCashOfferWithOpenStorage, getRemainingQuantityLabel, getTotalFees, getFinalPrice, getDelayedPriceContractDisplayText } from "./helpers";
import { getCommodityPriceIncrement, getAllowedPriceRange } from "features/orders/tasHelpers";
import ContractSubType from "features/contracts/contractSubType";
import { MerchantFeature, isDeferPayInterestEnabled, isFeatureEnabled } from "domain/merchant";
import { FeeThin } from "domain/fee";
import AddFee from "./AddFee";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import offerTypeHelper from "domain/offerTypeHelper";
import unitHelper from "domain/unitHelper";
import classNames from "classnames";
import useTranslators from "common/translation/useTranslators";
import useMarketSchedule from "features/systemConfig/useMarketSchedule";
import { hasPartialCents, roundCents } from "common/decimal";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { useAllowedPaymentOptions } from "features/merchants/usePaymentOptions";
import { Trans } from "react-i18next";
import { OfferField, OfferFormState } from "./useOfferBuilder";
import LocationPicker from "features/locations/LocationPicker";
import Toggle from "common/ui/Toggle";
import UiCustomization from "domain/uiCustomization";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { ErpSystem } from "domain/erpSystem";
import ContractExtendedType from "domain/contractExtendedType";

type AddOfferDetailsProps = {
    offer: Offer;
    formState: OfferFormState;
    offerTypes: OfferType[];
    contractExtendedTypes: ContractExtendedType[];
    customerWill: boolean[];
    traders: Trader[];
    deliveryLocations: PhysicalLocation[];
    inventoryLocations: PhysicalLocation[];
    contractLocations: PhysicalLocation[];
    regions: PhysicalLocation[];
    canChangeCommodity: boolean;
    commodities: Commodity[];
    deliveryMethods: DeliveryMethod[];
    deliveryPeriods: DeliveryPeriod[];
    futuresContracts: FuturesContract[];
    paymentOptions: PaymentOption[];
    delayedPriceContracts: Contract[];
    userIsTrader: boolean;
    canUseDetermineLater: boolean;
    canPushBasis: boolean;
    canManualEntry: boolean;
    canPlaceHedges: boolean;
    isInitialLocationDetermineLater?: boolean;
    remainingContractQuantity: number;
    isMarketOrder: boolean;
    setPriceLock(value: boolean, orderType: OrderType): void;
    onOfferUpdated(changedField: OfferField, offer: Partial<Offer>, form?: OfferFormState): void;
    doesCommodityBalanceExist: boolean;
    maxAllowedQuantity: number;
    showPaymentOption: boolean;
    timer: TimerResult;
    isPricingDelayedPriceContractModal?: boolean;
    isPricingWithBasisOffer?: boolean;
    isConvertBushelOnly?: boolean;
    realTimeFuturesPrice: number;
    allowPickingCustomer: boolean;
    fees: FeeThin[];
    priceAcrossLocations?: boolean;
    lockDeliveryMethod?: boolean;
    roundCashPrice?: boolean;
    contractSubTypes: ContractSubType[];
    uiCustomization: UiCustomization;
};

const AddOfferDetails: React.FC<AddOfferDetailsProps> = (props: AddOfferDetailsProps) => {
    const customerWillSellText = useCustomerWillSellText();

    //Offer types
    const isCash = offerTypeHelper.isCash(props.offer.type);
    const isHta = offerTypeHelper.isHta(props.offer.type);
    const isFixFutures = props.offer.type === OfferTypeEnum.FixFuturesOnBasis;
    const isFixBasis = props.offer.type === OfferTypeEnum.FixBasisOnHta;
    const isBasisOffer = props.offer.type === OfferTypeEnum.BasisOffer;
    const isDelayedPrice = props.offer.type === OfferTypeEnum.DelayedPriceContract;
    const isPricing = isFixFutures || isFixBasis;
    const isContract = !offerTypeHelper.originatesFromOffer(props.offer.type);
    const isBushelOnly = props.offer.type === OfferTypeEnum.BushelOnlyContract;
    const originalOfferType = React.useRef(props.offer.type);
    const isOriginalBushelOnly = originalOfferType.current === OfferTypeEnum.BushelOnlyContract;
    const [hasSelectedConvertType, setHasSelectedConvertType] = useState(false);
    //Order types
    const isMarketOrder = props.offer.orderType === OrderType.Market;
    const isLimitOrder = props.offer.orderType === OrderType.Limit || props.offer.orderType === OrderType.Iceberg;
    const isTasOrder = props.offer.orderType === OrderType.Tas;
    const isManualEntryOrder = props.offer.orderType === OrderType.ManualEntry;

    const selectedCommodity = props.offer.deliveryPeriod?.commodity ?? props.offer.commodity;
    const isFlatCash = selectedCommodity.isCashOnly;

    const isSetBasisFlow = isFixBasis || props.isPricingWithBasisOffer;

    const areLocationsDisabled = !props.canPushBasis && props.offer.isPriceLocked && isCash

    const [showCustomerPanel, setShowCustomerPanel] = useState(
        !isPricing ||
        isSetBasisFlow ||
        // determine later is not allowed for basis offers, default customer panel to expanded
        (props.isPricingWithBasisOffer && props.offer.location == null)
    );
    const [showExtendedFeesPanel, setShowExtendedFeesPanel] = useState(false);

    const marketSchedule = useMarketSchedule();

    const activeMerchant = useActiveMerchant();
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const userConfiguration = useSelector((m: IAppStore) => m.userState.userConfiguration);
    const displaySendToErpToggle = isFeatureEnabled(activeMerchant, MerchantFeature.SendToErpToggle);
    const shouldDisplayInterestStartDate = isDeferPayInterestEnabled(activeMerchant);
    const showLocalUOM = selectedCommodity?.baseUnit.id !== selectedCommodity?.localUnit.id;
    const unitsConversionRates = useUnitConversionRates();
    const availablePaymentOptionsDetails = useAllowedPaymentOptions(props.paymentOptions);
    const locationIsRequired = !props.canUseDetermineLater || !activeMerchant.isDetermineLaterAllowed || isFlatCash || isBasisOffer || props.isPricingWithBasisOffer;

    const isMaxQuantityReached = isCustomer && props.offer.producer?.maxOfferQuantity && props.offer.quantity > props.offer.producer.maxOfferQuantity;
    const quantityPrecision = props.uiCustomization.addEditOfferContractQuantityDecimalPrecision;

    function showPriceLockToggle(): boolean {
        if (isCustomer) return false;
        const { trader, type, futuresContract, commodity } = props.offer;
        return trader?.priceLock &&
            offerTypeHelper.isPriceLockAllowed(type) &&
            isMarketOrder &&
            futuresContract != null &&
            marketSchedule.isMarketOpen(commodity) &&
            !commodity.isCashOnly;
    }

    function canMovePriceByTick(): boolean {
        if (props.isConvertBushelOnly) return true;
        if (!props.offer.isPriceLocked) return false;

        if (!isHta && !isFixFutures) return false;

        const price = isFixFutures ? props.offer.futuresPrice : props.offer.cashPrice;
        return hasPartialCents(price, props.offer.deliveryPeriod?.commodity.bidPriceIncrement ?? .01);
    }

    function disablePriceLockToggle() {
        return props.offer.deliveryPeriod == null || props.offer.futuresContract == null;
    }

    function showFixBasisToggle() {
        if (isCustomer || isFixFutures || isManualEntryOrder || isBasisOffer || props.isPricingWithBasisOffer)
            return false;
        if (!(props.offer.deliveryMethod === DeliveryMethod.PickedUp || props.canPushBasis))
            return false;
        return true;
    }

    function getMaxQuantity() {
        if (isPricing || props.isConvertBushelOnly)
            return props.remainingContractQuantity;
        else if (showDelayedPriceContract() && props.offer.delayedContract != null)
            return props.offer.delayedContract.remainingQuantity;
        else if (props.isPricingDelayedPriceContractModal)
            return props.remainingContractQuantity;
        else if (props.doesCommodityBalanceExist)
            return props.maxAllowedQuantity;
        else
            return null;
    }

    function getOrderTypes(): OrderType[] {
        if (isFlatCash) {
            return FlatCashOrderTypes;
        }

        let orderTypes = null;
        if ((isCash || isFixFutures || isHta) && props.canManualEntry)
            orderTypes = AllOrderTypesWithManualEntry;
        else
            orderTypes = AllOrderTypes;

        if (!selectedCommodity?.isTasAllowed) {
            orderTypes = orderTypes.filter(ot => ot !== OrderType.Tas);
        }
        if (isOriginalBushelOnly || props.isConvertBushelOnly)
            orderTypes = orderTypes.filter(ot => ot !== OrderType.Limit);

        // converting a bushel only contract doesn't allow Limit above, and an iceberg is a limit order, so no Iceberg there either
        if (!isCustomer && props.canPlaceHedges && !isOriginalBushelOnly)
            orderTypes = [...orderTypes, OrderType.Iceberg];

        return orderTypes;
    }

    function disableBasisField() {
        if (isBasisOffer) return false;
        if (isCustomer) return true;
        if (isManualEntryOrder) return false;
        if (isFixFutures) return true;
        if ((props.canPushBasis || props.offer.deliveryMethod === DeliveryMethod.PickedUp) && props.offer.isBasisFixed)
            return false;
        return true;
    }

    function shouldShowFreightFee() {
        if (!props.userIsTrader)
            return false;

        return props.offer.customerWillSell ?
            props.offer.deliveryMethod === DeliveryMethod.PickedUp :
            props.offer.deliveryMethod === DeliveryMethod.Delivered;
    }

    function showDelayedPriceContract() {
        return props.offer.deliveryMethod === DeliveryMethod.DelayedPrice &&
            !isBushelOnly &&
            !props.isPricingDelayedPriceContractModal &&
            offerTypeHelper.canHaveDelayedPriceContract(props.offer.type) &&
            props.delayedPriceContracts?.length > 0;
    }

    function showRemainingQuantity() {
        return props.doesCommodityBalanceExist
            || props.isConvertBushelOnly
            || isPricing
            || props.isPricingDelayedPriceContractModal
            || (props.offer.deliveryMethod === DeliveryMethod.DelayedPrice && !isDelayedPrice)
            || props.priceAcrossLocations;
    }

    function shouldDisplayRegion() {
        return activeMerchant.generalSettings.displayRegion && !isCustomer &&
            props.regions.length > 0 &&
            props.offer.location?.homeLocation == null &&
            !props.regions.some(m => m.id === props.offer.location?.id);
    }

    function showPriceAtOpen(priceLock?: boolean, ordertype?: OrderType) {
        return !(priceLock ?? props.offer.isPriceLocked) &&
            (ordertype ?? props.offer.orderType) === OrderType.Market &&
            !marketSchedule.isMarketOpen(selectedCommodity);
    }

    function getQuantityInLocal(value: number) {
        return unitHelper.getLocalQuantity(value, selectedCommodity, unitsConversionRates, quantityPrecision);
    }

    function getPriceInLocal(value: number) {
        let localPrice = unitHelper.getLocalPrice(value, selectedCommodity, unitsConversionRates);
        if (props.roundCashPrice) {
            localPrice = roundCents(localPrice, props.offer.futuresContract?.isSettled
                ? props.offer.deliveryPeriod.commodity.roundingOnClose
                : props.offer.deliveryPeriod.commodity.roundingInSession)
        }
        return localPrice;
    }

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.offer.currency.code, selectedCommodity, unitsConversionRates);
    }

    function getFeesText() {
        const fees = getTotalFees(props.offer);
        return getPriceText(fees);
    }

    function isCashPriceDisabled(): boolean {
        if (isTasOrder && !props.formState.placeHedgeOrder)
            return true;

        if (canMovePriceByTick())
            return false;

        return props.isMarketOrder;
    }

    if (props.offer == null) return <></>;

    const priceRange = getAllowedPriceRange(isTasOrder, false, selectedCommodity);
    const isCustomerInSetFuturesOrSetBasisMode = isCustomer && isPricing;

    return <>
        {isPricing && <Form.Group as={Row} data-testid="contract-id-row">
            <Form.Label column>{isFixBasis ? localize("Details_Label_HtaContractId") : localize("Details_Label_BasisContractId")}</Form.Label>
            <Col xs="8">
                <div id="basisContractId" className="col-form-label">#{props.offer.displayId}</div>
            </Col>
        </Form.Group>}
        {!isPricing &&
            <Form.Group as={Row} data-testid="type-row">
                <Form.Label column>{props.isConvertBushelOnly ? localize("Details_Label_ConvertTo") : localize("Details_Label_Type")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        options={props.offerTypes}
                        value={props.isConvertBushelOnly && !hasSelectedConvertType ? null : { id: props.offer.type }}
                        valueGetter={m => m.id}
                        textGetter={m => m.name}
                        className={classNames({
                            "font-weight-bold": props.canPushBasis,
                            "bo-convert-required": props.isConvertBushelOnly && !hasSelectedConvertType
                        })}
                        showNullRow={props.isConvertBushelOnly}
                        nullRowText={localizeCommon("Value_Select")}
                        required
                        enforceValidOption={!props.isConvertBushelOnly}
                        onValueSelected={m => {
                            setHasSelectedConvertType(true);
                            props.onOfferUpdated(OfferField.Type, { type: m.id });
                            props.setPriceLock(
                                offerTypeHelper.isPriceLockAllowed(m.id) ? props.offer.isPriceLocked : false,
                                props.offer.orderType,
                            );
                        }}
                    />
                </Col>
            </Form.Group>
        }

        {isBushelOnly && (!props.isConvertBushelOnly || hasSelectedConvertType) &&
            <Form.Group as={Row} data-testid="type-row">
                <Form.Label column>{localize("Details_Label_ExtendedType")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.offer.contractExtendedType ?? null}
                        options={props.contractExtendedTypes}
                        showNullRow
                        nullRowText={localizeCommon("Value_Select")}
                        required
                        className={props.isConvertBushelOnly && !props.offer.contractExtendedType ? 'bo-convert-required' : ''}
                        valueGetter={m => m.externalId}
                        textGetter={m => m.name}
                        onValueSelected={m => {
                            props.onOfferUpdated(OfferField.ContractExtendedType, { contractExtendedType: m });
                        }} />
                </Col>
            </Form.Group>
        }

        <Form.Group as={Row} className={!showCustomerPanel && "mb-0"} data-testid="acct-id-customer-row">
            {isPricing && <Form.Label column="sm" sm={1} className="text-left">
                <span onClick={() => setShowCustomerPanel(!showCustomerPanel)} aria-controls="customer-collapse" aria-expanded={showCustomerPanel}>
                    <i className="material-icons">{showCustomerPanel ? 'expand_less' : 'expand_more'}</i>
                </span>
            </Form.Label>}
            {!isCustomer && props.allowPickingCustomer && <>
                <Form.Label column>{localize("Details_Label_AccountCustomer")}</Form.Label>
                <Col xs="8">
                    {(isPricing || props.isPricingDelayedPriceContractModal) ?
                        <div className="col-form-label">{getProducerDisplayName(props.offer.producer)}</div>
                        : <CustomerAccountPicker
                            showClearButton
                            inputProps={{ required: true, 'aria-label': "Customer" }}
                            selected={props.offer.producer}
                            selectedAccount={props.offer.customerAccount?.accountId}
                            onCustomerSelected={(c, a) => props.onOfferUpdated(OfferField.CustomerAccount, { producer: c, customerAccount: a })}
                        />}
                </Col>
            </>}
            {!isCustomerInSetFuturesOrSetBasisMode && (isCustomer || !props.allowPickingCustomer) && <>
                <Form.Label column>{localize("Details_Label_Customer")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getProducerDisplayName(props.offer.producer)}</div>
                </Col>
            </>
            }
            {isCustomerInSetFuturesOrSetBasisMode && <AccountIdSelect {...props} />}
        </Form.Group>
        {!isCustomerInSetFuturesOrSetBasisMode && (isCustomer || (!props.allowPickingCustomer && props.offer.producer?.accounts != null)) &&
            <Form.Group as={Row} data-testid="acct-id-customer-select-row">
                <AccountIdSelect {...props} />
            </Form.Group>
        }

        {activeMerchant.generalSettings.displayErpContractId && !isCustomer && props.offer.sourceContractId == null &&
            <Form.Group as={Row} data-testid="erp-contract-id-row">
                <Form.Label column className="optional">{localize("Details_Label_ErpContractId")}</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.offer.erpContractId}
                        onChange={ev => props.onOfferUpdated(OfferField.ErpContractId, { erpContractId: ev.target.value })}>
                    </Form.Control>
                </Col>
            </Form.Group>}

        <Form.Group as={Row} data-testid="customer-will-row">
            <Form.Label column>{customerWillSellText.label}</Form.Label>
            <Col xs="8">
                {isPricing
                    ? <div className="col-form-label">{customerWillSellText.formatValue(props.offer.customerWillSell)}</div>
                    : <TypedSelect
                        options={props.customerWill}
                        value={props.offer.customerWillSell}
                        disabled={isOriginalBushelOnly || !(userConfiguration.canBuy && userConfiguration.canSell)}
                        valueGetter={m => m}
                        textGetter={customerWillSellText.formatValue}
                        onValueSelected={m => props.onOfferUpdated(OfferField.CustomerWill, { customerWillSell: m })} />}
            </Col>
        </Form.Group>

        <Collapse in={showCustomerPanel} className={isPricing && "border-top border-bottom pt-2 pb-1 mb-2"}>
            <div id="customer-collapse">

                <Form.Group as={Row} data-testid="commodity-row">
                    <Form.Label column>{localize("Details_Label_Commodity")}</Form.Label>
                    <Col xs="8">
                        {isPricing ?
                            <div className="col-form-label">{selectedCommodity.name}</div>
                            : <TypedSelect
                                disabled={!props.canChangeCommodity || isCustomer}
                                value={selectedCommodity}
                                options={props.commodities}
                                valueGetter={c => c.id}
                                textGetter={c => c.displayName}
                                onValueSelected={c => props.onOfferUpdated(OfferField.Commodity, { commodity: c })} />}
                    </Col>
                </Form.Group>

                <Form.Group as={Row} data-testid="location-row">
                    <Form.Label column>{localize("Details_Label_Location")}</Form.Label>
                    <Col xs="8">
                        {(isPricing && !isSetBasisFlow && !props.isInitialLocationDetermineLater) ?
                            <div className="col-form-label">{props.offer.location?.name}</div>
                            : <LocationPicker
                                disabled={props.priceAcrossLocations || areLocationsDisabled}
                                required={locationIsRequired}
                                locations={props.deliveryLocations}
                                showDetermineLater={!locationIsRequired}
                                selected={props.offer.location}
                                onLocationSelected={l => props.onOfferUpdated(OfferField.Location, { location: l })}
                            />}
                    </Col>
                </Form.Group>

                {activeMerchant.generalSettings.displayInventoryLocation && !isCustomer && <Form.Group as={Row} data-testid="inventory-location-row">
                    <Form.Label column>{localize("Details_Label_InventoryLocation")}</Form.Label>
                    <Col xs="8">
                        <LocationPicker
                            disabled={areLocationsDisabled || (props.offer.location == null && locationIsRequired)}
                            required={locationIsRequired}
                            locations={props.inventoryLocations}
                            showDetermineLater={!locationIsRequired}
                            selected={props.offer.inventoryLocation}
                            onLocationSelected={l => props.onOfferUpdated(OfferField.InventoryLocation, { inventoryLocation: l })}
                        />
                    </Col>
                </Form.Group>}

                {activeMerchant.generalSettings.displayContractLocation && !isCustomer && <Form.Group as={Row} data-testid="contract-location-row">
                    <Form.Label column>{localize("Details_Label_ContractLocation")}</Form.Label>
                    <Col xs="8">
                        {(isPricing && !props.isInitialLocationDetermineLater) ?
                            <div className="col-form-label">{props.offer.contractLocation?.name}</div>
                            : <LocationPicker
                                disabled={areLocationsDisabled || (props.offer.inventoryLocation == null && locationIsRequired)}
                                required={locationIsRequired}
                                locations={props.contractLocations}
                                showDetermineLater={!locationIsRequired}
                                selected={props.offer.contractLocation}
                                onLocationSelected={l => props.onOfferUpdated(OfferField.ContractLocation, { contractLocation: l })}
                            />}
                    </Col>
                </Form.Group>}

                {shouldDisplayRegion() && <Form.Group as={Row} data-testid="region-row">
                    <Form.Label column>{localize("Details_Label_Region")}</Form.Label>
                    <Col xs="8">
                        <LocationPicker
                            required
                            locations={props.regions}
                            selected={props.offer.region}
                            onLocationSelected={l => { props.onOfferUpdated(OfferField.Region, { region: l }) }}
                        />
                    </Col>
                </Form.Group>}

                {showDelayedPriceContract() && <Form.Group as={Row} data-testid="contract-delivery-period-row">
                    <Form.Label column>{localize("Details_Label_DeliveryPeriodContract")}</Form.Label>
                    <Col xs="8">
                        <TypedSelect
                            value={props.offer.delayedContract}
                            options={props.delayedPriceContracts}
                            showNullRow={props.delayedPriceContracts?.length === 0}
                            nullRowText={localizeCommon("Value_Select")}
                            required
                            valueGetter={m => m.internalId}
                            textGetter={getDelayedPriceContractDisplayText}
                            onValueSelected={dc => props.onOfferUpdated(OfferField.DPContract, { delayedContract: dc, sourceContractId: dc?.internalId, quantity: dc?.remainingQuantity })} />
                    </Col>
                </Form.Group>}

                {!isBushelOnly && !isDelayedPrice && <Form.Group as={Row} data-testid="delivery-period-row">
                    <Form.Label column>{localize("Details_Label_DeliveryPeriod")}</Form.Label>
                    <Col xs="8">
                        {(isPricing && !isSetBasisFlow) ?
                            <div className="col-form-label">{props.offer.deliveryPeriod == null ? localizeCommon("Value_Select") :
                                getDeliveryPeriodDisplayName(props.offer.deliveryPeriod)}</div>
                            : <TypedSelect
                                value={props.offer.deliveryPeriod}
                                options={props.deliveryPeriods}
                                showNullRow
                                nullRowText={localizeCommon("Value_Select")}
                                required
                                className={props.isConvertBushelOnly && !props.offer.deliveryPeriod ? 'bo-convert-required' : ''}
                                valueGetter={dp => dp.id}
                                textGetter={dp => getDeliveryPeriodDisplayName(dp)}
                                disabled={!props.canPushBasis && props.offer.isPriceLocked && isCash}
                                onValueSelected={dp => {
                                    props.onOfferUpdated(OfferField.DeliveryPeriod, { deliveryPeriod: dp });

                                    if (dp?.contract?.symbol !== props.offer.deliveryPeriod?.contract?.symbol)
                                        props.setPriceLock(props.offer.isPriceLocked, props.offer.orderType);
                                }} />}
                    </Col>
                </Form.Group>
                }

                {!isPricing && <DeliveryMethodComponent {...props} />}

                {shouldShowFreightFee() && <Form.Group as={Row} data-testid="freight-fee-row">
                    <Form.Label column className="optional">{localize("Details_Label_FreightFee")}</Form.Label>
                    <Col xs="8">
                        <NumberEditor className='form-control'
                            min={0}
                            step={.01}
                            allowDecimals
                            precision={4}
                            enforcePrecision
                            value={props.offer.freightFee}
                            onValueChange={value => { props.onOfferUpdated(OfferField.FreightFee, { freightFee: value }); }}
                        />
                    </Col>
                </Form.Group>}
            </div>
        </Collapse>

        {isPricing && <DeliveryMethodComponent {...props} />}

        <Form.Group as={Row} data-testid="start-date-row">
            <Form.Label column>{localize("Details_Label_StartDate")}</Form.Label>
            <Col xs="8">
                {!isCustomer
                    ? <Form.Control type="date"
                        className={props.isConvertBushelOnly && !props.offer.deliveryPeriodStartDate ? 'bo-convert-required' : ''}
                        value={formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}
                        required
                        onChange={ev => ev.target.value &&
                            props.onOfferUpdated(OfferField.StartDate, {
                                deliveryPeriodStartDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate()
                            })}>
                    </Form.Control>
                    : <div className="col-form-label">{formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}</div>
                }
            </Col>
        </Form.Group>

        <Form.Group as={Row} data-testid="end-date-row">
            <Form.Label column>{localize("Details_Label_EndDate")}</Form.Label>
            <Col xs="8">
                {!isCustomer
                    ? <Form.Control type="date"
                        className={props.isConvertBushelOnly && !props.offer.deliveryPeriodEndDate ? 'bo-convert-required' : ''}
                        value={formatUTCDate(props.offer.deliveryPeriodEndDate, "yyyy-MM-DD")}
                        required
                        min={formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}
                        onChange={ev => ev.target.value &&
                            props.onOfferUpdated(OfferField.EndDate, { deliveryPeriodEndDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                    </Form.Control>
                    : <div className="col-form-label">{formatUTCDate(props.offer.deliveryPeriodEndDate, "yyyy-MM-DD")}</div>
                }
            </Col>
        </Form.Group>

        <Form.Group as={Row} className={!showExtendedFeesPanel && props.offer.fees.length > 0 && "mb-0"} data-testid="total-fees-row">
            {(isCustomer ? props.offer.fees : props.fees).length > 0 && <Form.Label column className="text-left">
                <span onClick={() => setShowExtendedFeesPanel(!showExtendedFeesPanel)} aria-controls="fees-collapse" aria-expanded={showExtendedFeesPanel}>
                    <i className="material-icons">{showExtendedFeesPanel ? 'expand_less' : 'expand_more'}</i>
                </span>
            </Form.Label>}
            <Form.Label column className="pl-0">{localize("Details_Label_TotalFees")}</Form.Label>
            <Col xs="8">
                <div className="col-form-label">{getFeesText()}</div>
            </Col>
        </Form.Group>

        <Collapse in={showExtendedFeesPanel} className={"border-top border-bottom pt-2 pb-1 mb-2"}>
            <div id="fees-collapse">
                <AddFee fees={props.fees}
                    selectedFees={props.offer.fees}
                    date={props.offer.deliveryPeriodStartDate}
                    disabled={isCustomer}
                    onFeesChanged={(fees) => props.onOfferUpdated(OfferField.Fees, { fees: fees })}
                />
            </div>
        </Collapse>

        {!isBushelOnly && !isContract && !isFixBasis && !isBasisOffer && !isCustomer && <Form.Group as={Row} data-testid="order-type-row">
            <Form.Label column>{localize("Details_Label_PricingType")}</Form.Label>
            <Col xs="4">
                <TypedSelect
                    options={getOrderTypes()}
                    value={props.offer.orderType}
                    valueGetter={o => o}
                    textGetter={o => getOfferContractOrderTypeName(o)}
                    className="font-weight-bold pl-2 pricing-type-select"
                    enforceValidOption
                    onValueSelected={o => {
                        const isMarket = o === OrderType.Market;
                        let priceLock = props.offer.isPriceLocked;
                        if (props.offer.isPriceLocked && !isMarket) priceLock = false;
                        if (props.offer.trader?.priceLock != null &&
                            isMarket &&
                            marketSchedule.isMarketOpen(selectedCommodity) &&
                            !props.offer.commodity?.isCashOnly
                        ) {
                            priceLock = true;
                        }
                        props.onOfferUpdated(OfferField.OrderType, {
                            orderType: o,
                            isPriceAtOpenEnabled: showPriceAtOpen(priceLock, o) ? activeMerchant.generalSettings.isPriceAtOpenEnabled : false,
                        });
                        props.setPriceLock(priceLock, o);
                    }} />
            </Col>
            <Col xs="4" className={classNames({ 'disabled-control': disablePriceLockToggle() }, "p-0")}>
                {showPriceLockToggle() && <>
                    <OverlayTrigger overlay={
                        disablePriceLockToggle() ? <Tooltip id={`tooltip-price-lock`}>
                            {localize("Details_Tooltip_PriceLock")}
                        </Tooltip> : <span></span>
                    }>
                        <span>
                            <Form.Label>{localize("Details_Label_PriceLock")}</Form.Label> &nbsp;
                            <Toggle isChecked={props.offer.isPriceLocked}
                                isDisabled={disablePriceLockToggle()}
                                onChange={checked => props.setPriceLock(checked, props.offer.orderType)}
                            />
                        </span>
                    </OverlayTrigger>

                    {props.offer.isPriceLocked && <CountdownTimer timer={props.timer} />}
                </>}
                {showPriceAtOpen() && <>
                    <Form.Label>{localize("Details_Label_PriceAtOpen")}</Form.Label>&nbsp;
                    <Toggle isChecked={props.offer.isPriceAtOpenEnabled}
                        onChange={checked => props.onOfferUpdated(OfferField.PriceAtOpen, { isPriceAtOpenEnabled: checked })} />
                </>}
            </Col>
        </Form.Group>}

        {!isDelayedPrice && !isFlatCash && <Form.Group as={Row} data-testid="symbol-row">
            <Form.Label column>{localize("Details_Label_Symbol")}</Form.Label>
            <Col xs="8">
                {isPricing ?
                    <div className="col-form-label">{props.offer.futuresContract?.symbol}</div>
                    : <TypedSelect
                        disabled={isCustomer}
                        showNullRow={props.canPushBasis}
                        nullRowText={localizeCommon("Value_Select")}
                        required
                        className={props.isConvertBushelOnly && !props.offer.futuresContract ? 'bo-convert-required' : ''}
                        value={props.offer.futuresContract}
                        options={props.futuresContracts}
                        valueGetter={fc => fc.id}
                        textGetter={fc => fc.symbol}
                        onValueSelected={fc => {
                            props.onOfferUpdated(OfferField.Symbol, { futuresContract: fc });
                            props.setPriceLock(props.offer.isPriceLocked, props.offer.orderType);
                        }}
                    />}
            </Col>
        </Form.Group>}

        {!isHta && !isDelayedPrice && !isFlatCash && <Form.Group as={Row} className="align-items-end" data-testid="basis-row">
            <Form.Label column>{localize("Details_Label_Basis")}</Form.Label>
            <Col xs="4">
                <NumberEditor
                    className={`form-control ${props.isConvertBushelOnly && !isBushelOnly && (props.offer.basis == null) ? 'bo-convert-required' : ''}`}
                    disabled={disableBasisField()}
                    required={props.offer.isBasisFixed}
                    step={.01}
                    allowDecimals
                    precision={5}
                    enforcePrecision
                    value={props.offer.basis ?? ''}
                    onValueChange={value => props.onOfferUpdated(OfferField.Basis, { basis: value, isBasisFixed: true })}
                    placeholder={'--'}
                />
            </Col>
            <Col xs="4" className="p-0">
                {showFixBasisToggle() && <>
                    <Form.Label>{localize("Details_Label_SetBasis")}</Form.Label>&nbsp;
                    <Toggle className="d-inline-block" isChecked={props.offer.isBasisFixed}
                        onChange={checked => props.onOfferUpdated(OfferField.SetBasis, { isBasisFixed: checked })} />
                </>}
            </Col>
        </Form.Group>}

        {(!isTasOrder && !isFlatCash && (isPricing || isManualEntryOrder ||
            (isLimitOrder && (isCash || isHta)))) &&
            <Form.Group as={Row} data-testid="curr-futures-row">
                <Form.Label column>{localize("Details_Label_CurrentFutures")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">
                        {formatNumber(props.realTimeFuturesPrice, "0,0.0000")}
                    </div>
                </Col>
            </Form.Group>
        }
        {!isTasOrder && !isFlatCash && (isBushelOnly || isPricing || isManualEntryOrder ||
            (isCash && isLimitOrder)) &&
            <Form.Group as={Row} data-testid="req-futures-row">
                <Form.Label column>{isBushelOnly ? localize("Strings_Value_FuturesPrice") : localize("Details_Label_RequiredFutures")}</Form.Label>
                <Col xs="4">
                    {(isFixBasis || (isCash && isLimitOrder))
                        ? <div className="col-form-label">{formatNumber(props.offer.futuresPrice, "0,0.0000")}</div>
                        : <NumberEditor
                            className={(() => {
                                const classes = [props.formState.hasUserUpdatedCashPrice ? 'font-weight-bold' : null];
                                const shouldHighlight = props.isConvertBushelOnly && !isBushelOnly && isManualEntryOrder && (props.offer.futuresPrice == null);
                                if (shouldHighlight) classes.push('bo-convert-required');
                                return classes.filter(Boolean).join(' ');
                            })()}
                            disabled={canMovePriceByTick() ? false : props.isMarketOrder}
                            required={!props.isMarketOrder && !isBushelOnly}
                            min={0}
                            step={getCommodityPriceIncrement(selectedCommodity, props.offer.orderType) ?? .01}
                            allowDecimals
                            precision={5}
                            enforcePrecision
                            roundingCent
                            value={props.offer.futuresPrice}
                            onValueChange={value => props.onOfferUpdated(OfferField.FuturesPrice, { futuresPrice: value })}
                        />}
                </Col>
                <Col xs="4">
                </Col>
            </Form.Group>
        }

        {!isContract && !isBasisOffer && <Form.Group as={Row} data-testid="price-row">
            <Form.Label column>{getPriceLabelForOffer(props.offer.type, props.offer.orderType)}</Form.Label>
            <Col xs="4">
                <CashPrice value={props.offer.cashPrice}
                    isPricingType={isPricing && !isTasOrder}
                    isManualEntry={isManualEntryOrder}
                    formState={props.formState}
                    isMarketOrder={props.isMarketOrder}
                    isDisabled={isCashPriceDisabled()}
                    priceIncrement={getCommodityPriceIncrement(selectedCommodity, props.offer.orderType) ?? .01}
                    min={priceRange.min ?? 0}
                    max={priceRange.max}
                    isPriceLocked={props.offer.isPriceLocked}
                    onValueChanged={value => {
                        const futuresPrice = NP.minus(value ?? 0, props.offer.basis ?? 0);
                        props.onOfferUpdated(OfferField.CashPrice, { cashPrice: value, futuresPrice }, { hasUserUpdatedCashPrice: true });
                    }}
                    currencyCode={props.offer.currency.code}
                    canMovePriceByTick={canMovePriceByTick()}
                />
            </Col>
            <Col xs="4" className="pl-0">
                {showLocalUOM && !isTasOrder && <CashPrice value={getPriceInLocal(props.offer.cashPrice)}
                    isPricingType={isPricing && !isTasOrder}
                    isManualEntry={isManualEntryOrder}
                    formState={props.formState}
                    isMarketOrder={props.isMarketOrder}
                    isDisabled={isCashPriceDisabled()}
                    min={priceRange.min ?? 0}
                    max={priceRange.max}
                    isPriceLocked={props.offer.isPriceLocked}
                    onValueChanged={value => {
                        const basePrice = unitHelper.getBasePrice(value, selectedCommodity, unitsConversionRates);
                        const futuresPrice = NP.minus(basePrice ?? 0, props.offer.basis ?? 0);
                        props.onOfferUpdated(OfferField.CashPrice, { cashPrice: basePrice, futuresPrice });
                    }}
                    currencyCode={props.offer.currency.code}
                    canMovePriceByTick={canMovePriceByTick()}
                />}</Col>
        </Form.Group>}
        {!(isContract || isFixBasis || isBasisOffer) && isCustomer && <Form.Group as={Row} data-testid="at-market-row">
            <Form.Label column></Form.Label>
            <Col xs="8">
                <Form.Label>{localize("Details_Label_AtMarket")}</Form.Label>&nbsp;
                <Toggle isChecked={isMarketOrder}
                    onChange={checked => props.onOfferUpdated(OfferField.AtMarket, { orderType: checked ? OrderType.Market : OrderType.Limit })} />
            </Col>
        </Form.Group>}

        {isTasOrder && (
            <Form.Group as={Row} data-testid="tas-not-fill-row">
                <Form.Label column>{localize("Details_Label_TasDoesNotFill")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.offer.tasFallbackOption}
                        options={AllTasFallbackOptions}
                        valueGetter={o => o}
                        textGetter={o => getTasFallbackOptionLabel(o)}
                        enforceValidOption
                        onValueSelected={value => {
                            props.onOfferUpdated(OfferField.TasFallbackOption, { tasFallbackOption: value });
                        }}
                    />
                </Col>
            </Form.Group>
        )}

        {props.offer.deliveryPeriod?.premium != null && <Form.Group as={Row} data-testid="premium-row">
            <Form.Label column>{localize("Details_Label_Premium")}</Form.Label>
            <Col xs="8">
                <div className="col-form-label">{getPriceText(props.offer.deliveryPeriod?.premium)}</div>
            </Col>
        </Form.Group>}

        {!isBushelOnly && !isContract && !isBasisOffer && !isTasOrder && <Form.Group as={Row} data-testid="final-price-row">
            <Form.Label column>{localize("Details_Label_FinalPrice")}</Form.Label>
            <Col xs="8">
                <div className="col-form-label">{getPriceText(getFinalPrice(props.offer))}</div>
            </Col>
        </Form.Group>}

        <Form.Group as={Row} data-testid="quantity-row">
            <Form.Label column>{localize("Details_Label_Quantity")}</Form.Label>
            <Col xs={showLocalUOM ? "4" : "8"}>
                <NumberEditor
                    className={classNames('form-control', {
                        "quantity-alert max-quantity-reached": isMaxQuantityReached,
                        "bo-convert-required": props.isConvertBushelOnly && (props.offer.quantity == null)
                    })}
                    aria-label="Quantity"
                    required
                    allowDecimals
                    precision={quantityPrecision}
                    thousandSeparator
                    unit={selectedCommodity.baseUnit.abbreviation}
                    min={0}
                    disabled={props.doesCommodityBalanceExist && props.maxAllowedQuantity === 0}
                    isInvalidMessage={localize("Details_Validation_OfferExceedsMax")}
                    max={getMaxQuantity()}
                    value={props.offer.quantity}
                    onValueChange={value => props.onOfferUpdated(OfferField.Quantity, { quantity: value })}
                    validationName="quantity"
                    type="text"
                />
            </Col>
            {showLocalUOM && <Col xs="4" className="pl-0">
                <NumberEditor
                    className={classNames('form-control', {
                        "quantity-alert max-quantity-reached": isMaxQuantityReached,
                        "bo-convert-required": props.isConvertBushelOnly && (props.offer.quantity == null)
                    })}
                    aria-label="Quantity-Local"
                    required
                    allowDecimals
                    precision={quantityPrecision}
                    thousandSeparator
                    unit={selectedCommodity.localUnit.abbreviation}
                    min={0}
                    disabled={props.doesCommodityBalanceExist && props.maxAllowedQuantity === 0}
                    isInvalidMessage={localize("Details_Validation_OfferExceedsMax")}
                    max={getQuantityInLocal(getMaxQuantity())}
                    value={getQuantityInLocal(props.offer.quantity)}
                    onValueChange={value => props.onOfferUpdated(
                        OfferField.Quantity,
                        { quantity: unitHelper.getBaseQuantity(value, selectedCommodity, unitsConversionRates, quantityPrecision) },
                    )}
                    validationName="quantity"
                />
            </Col>}
        </Form.Group>

        {activeMerchant.erpSystem === ErpSystem.AgrisDataHub && isPricing &&
            <Form.Group as={Row} data-testid="use-unapplied-balance-row">
                <Form.Label column></Form.Label>
                <Col xs="8">
                    <Form.Check
                        label="Use Unapplied Balance"
                        checked={props.offer.useUnappliedBalance}
                        onChange={ev => props.onOfferUpdated(OfferField.UseUnappliedBalance, { useUnappliedBalance: ev.target.checked })}
                    />
                </Col>
            </Form.Group>
        }

        {showRemainingQuantity() && <Form.Group as={Row} data-testid="remaining-quantity-row">
            <Form.Label column> {getRemainingQuantityLabel(props.offer.type, props.offer.deliveryMethod)}</Form.Label>
            <Col xs="8">
                <div className="col-form-label">
                    {props.doesCommodityBalanceExist
                        ? formatQuantity(NP.minus(props.maxAllowedQuantity ?? 0, props.offer.quantity ?? 0), quantityPrecision)
                        : formatQuantity(NP.minus(props.remainingContractQuantity ?? 0, props.offer.quantity ?? 0), quantityPrecision)}
                </div>
            </Col>
        </Form.Group>}

        {isCustomer && <Alert variant='primary' className="d-flex align-items-center quantity-alert">
            <i className="material-icons info-icon mr-2 quantity-alert">info</i>
            {
                !props.offer.producer?.maxOfferQuantity ?
                    <span>{localize("Details_Label_RequireApprovalInformation")}</span>
                    :
                    <span><Trans i18nKey={'Trans_Label_OverMaxOfferQuantityInformation'} ns="Offers&Contracts">Offers over <strong>{{ max: formatQuantity(props.offer.producer.maxOfferQuantity, '0,0') }}</strong> require approval and once submitted, they can be found in Pending Offers. </Trans></span>
            }
        </Alert>}

        {!isTasOrder && !isMarketOrder && !isContract && !isManualEntryOrder &&
            <Form.Group as={Row} data-testid="expiration-date-row">
                <Form.Label column>{localize("Details_Label_Expiration")}</Form.Label>
                <Col xs="8">
                    <Form.Control type="date"
                        value={formatUTCDate(props.offer.expirationDate, "yyyy-MM-DD")}
                        required
                        max={formatDate(isCashOfferWithOpenStorage(props.offer) ? toUtcDay(props.offer.deliveryPeriod?.end) : getPreviousDayForGivenDate(toUtcDay(props.offer.futuresContract?.expiration)), "yyyy-MM-DD")}
                        onChange={ev => ev.target.value && props.onOfferUpdated(OfferField.Expiration, { expirationDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                    </Form.Control>
                </Col>
            </Form.Group>
        }

        {!isCash && <Form.Group as={Row} data-testid="final-pricing-date-row">
            <Form.Label column>{localize("Details_Label_FinalPricingDate")}</Form.Label>
            <Col xs="8">
                <Form.Control type="date"
                    value={formatUTCDate(props.offer.finalPricingDate, "yyyy-MM-DD")}
                    required
                    max={formatDate(isCashOfferWithOpenStorage(props.offer) ? toUtcDay(props.offer.deliveryPeriod?.end) : getPreviousDayForGivenDate(toUtcDay(props.offer.futuresContract?.expiration)), "yyyy-MM-DD")}
                    onChange={ev => ev.target.value && props.onOfferUpdated(OfferField.FinalPricingDate, { finalPricingDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                </Form.Control>
            </Col>
        </Form.Group>}

        {!isCustomer && <Form.Group as={Row} data-testid="trader-row">
            <Form.Label column>{localize("Details_Label_Trader")}</Form.Label>
            <Col xs="8">
                <TypedSelect
                    value={props.offer.trader}
                    required
                    className={props.isConvertBushelOnly && !props.offer.trader ? 'bo-convert-required' : ''}
                    options={props.traders}
                    valueGetter={m => m.userId}
                    textGetter={m => `${m.firstName} ${m.lastName}`}
                    onValueSelected={m => props.onOfferUpdated(OfferField.Trader, { trader: m })} />
            </Col>
        </Form.Group>}

        {!isBushelOnly && props.showPaymentOption &&
            <Form.Group as={Row} data-testid="payment-option-row">
                <Form.Label column>{localize("Details_Label_PaymentOption")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.offer.paymentOption}
                        options={availablePaymentOptionsDetails}
                        valueGetter={p => p?.paymentOption ?? p}
                        textGetter={p => p.displayName}
                        onValueSelected={p => props.onOfferUpdated(OfferField.PaymentOption, {
                            ...props.offer,
                            paymentOption: p.paymentOption
                        })} />
                </Col>
            </Form.Group>
        }

        {props.contractSubTypes.length > 1 && props.offer.deliveryMethod !== DeliveryMethod.DelayedPrice &&
            <Form.Group as={Row} data-testid="contract-sub-type-row">
                <Form.Label column>{localize("Details_Label_ContractSubType")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        required
                        showNullRow
                        value={{ value: props.offer.contractSubType }}
                        options={props.contractSubTypes}
                        valueGetter={p => p.value}
                        textGetter={p => p.name}
                        onValueSelected={p => props.onOfferUpdated(OfferField.ContractSubType, { contractSubType: p.value })} />
                </Col>
            </Form.Group>}

        {props.offer.paymentOption === PaymentOption.DeferredPay && <div>
            <Form.Group as={Row} data-testid="pay-date-row">
                <Form.Label column>{localize("Details_Label_PayDate")}</Form.Label>
                <Col xs="8">
                    <Form.Control type="date"
                        value={props.offer.deferredPayDate}
                        required
                        min={formatDate(getNextDay(), "yyyy-MM-DD")}
                        onChange={ev => ev.target.value && props.onOfferUpdated(OfferField.PayDate, { deferredPayDate: ev.target.value })}>
                    </Form.Control>
                </Col>
            </Form.Group>

            {shouldDisplayInterestStartDate && <Form.Group as={Row} data-testid="interest-start-date-row">
                <Form.Label column className="optional">{localize("Details_Label_InterestStartDate")}</Form.Label>
                {isLimitOrder && <Col xs="4" className="pt-2">
                    <Toggle isChecked={props.offer.setInterestStartDateOnDateOfFill}
                        onChange={checked => {
                            props.onOfferUpdated(OfferField.InterestStartDate, {
                                setInterestStartDateOnDateOfFill: checked,
                                interestStartDate: checked ? null : props.offer.interestStartDate
                            })
                        }} />&nbsp;
                    <Form.Label className="optional">{localize("Details_Label_OnDateOfFill")}</Form.Label>
                </Col>}
                <Col xs={isLimitOrder ? "4" : "8"}>
                    {!props.offer.setInterestStartDateOnDateOfFill && <Form.Control type="date"
                        value={props.offer.interestStartDate}
                        min={formatTodaysDate('yyyy-MM-DD')}
                        max={props.offer.deferredPayDate}
                        onChange={ev => ev.target.value && props.onOfferUpdated(OfferField.InterestStartDate, { interestStartDate: ev.target.value })}>
                    </Form.Control>}
                </Col>
            </Form.Group>}

            <Form.Group as={Row} data-testid="interest-rate-row">
                <Form.Label column className="optional">{localize("Details_Label_InterestRate")}</Form.Label>
                <Col xs="4">
                    {activeMerchant.generalSettings.defaultDeferPayInterestRate != null
                        ? <div className="col-form-label">{activeMerchant.generalSettings.defaultDeferPayInterestRate} %</div>
                        : <InputGroup className="rate-input">
                            <NumberEditor className="form-control"
                                aria-label="InterestRate"
                                allowDecimals
                                precision={4}
                                min={0}
                                max={100}
                                placeholder={"0.0000"}
                                value={props.offer.interestRate}
                                onValueChange={value => props.onOfferUpdated(OfferField.InterestRate, { interestRate: value })}
                            />
                            <InputGroup.Append className="rate-text">
                                <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup.Append></InputGroup>
                    }
                </Col>
                <Col></Col>
            </Form.Group>
        </div>}

        <Form.Group as={Row} data-testid="comment-row">
            <Form.Label column className="optional">{localize("Details_Label_Comment")}</Form.Label>
            <Col xs="8">
                <Form.Control
                    value={props.offer.comments}
                    onChange={ev => props.onOfferUpdated(OfferField.Comment, { comments: ev.target.value })}>
                </Form.Control>
            </Col>
        </Form.Group>

        {props.formState.displayDeliveryTerms && <Form.Group as={Row} data-testid="delivery-terms-row">
            <Form.Label column className="optional">{localize("Details_Label_DeliveryTerms")}</Form.Label>
            <Col xs="8">
                {isPricing ?
                    <div className="col-form-label">{props.offer.deliveryTerms}</div> :
                    <Form.Control
                        value={props.offer.deliveryTerms}
                        onChange={ev => props.onOfferUpdated(OfferField.DeliveryTerms, { deliveryTerms: ev.target.value })}>
                    </Form.Control>
                }
            </Col>
        </Form.Group>}

        {props.formState.displayCustomerContractId && <Form.Group as={Row} data-testid="cust-contract-id-row">
            <Form.Label column className="optional">{localize("Details_Label_CustomerContractId")}</Form.Label>
            <Col xs="8">
                <Form.Control
                    value={props.offer.customerContractId}
                    required={activeMerchant.generalSettings.makeCustomerContractIdRequired}
                    onChange={ev => props.onOfferUpdated(OfferField.CustomerContractId, { customerContractId: ev.target.value })}>
                </Form.Control>
            </Col>
        </Form.Group>}

        {!isContract && !isCustomer && <Form.Group as={Row} data-testid="send-notification-row">
            <Col xs="12" className="text-right">
                <Form.Label>{localize("Details_Label_SendNotificationToCustomer")}</Form.Label>&nbsp;
                <Toggle isChecked={props.offer.notifyCustomer}
                    onChange={checked => props.onOfferUpdated(OfferField.NotifyCustomer, { notifyCustomer: checked })} />
            </Col>
        </Form.Group>}
        {props.formState.shouldShowHedgeOrderToggle &&
            <Form.Group as={Row} data-testid="place-hedge-row">
                <Col xs="12" className="text-right">
                    <Form.Label className="primary-text font-weight-bold">{localize("Details_Label_PlaceHedgeOrder")}</Form.Label>&nbsp;
                    <Toggle isChecked={props.formState.placeHedgeOrder}
                        onChange={checked => props.onOfferUpdated(OfferField.PlaceHedge, {}, { placeHedgeOrder: checked })} />
                </Col>
            </Form.Group>
        }
        {displaySendToErpToggle && activeMerchant.hasErpIntegration && !isCustomer && <Form.Group as={Row} data-testid="send-erp-row">
            <Col xs="12" className="text-right">
                <Form.Label>{localize("Details_Label_SendToErp")}</Form.Label>&nbsp;
                <Toggle isChecked={!props.offer.disableErpSync}
                    onChange={checked => props.onOfferUpdated(OfferField.DisableErpSync, { disableErpSync: !checked })} />
            </Col>
        </Form.Group>}
    </>;
}

const AccountIdSelect: React.FC<AddOfferDetailsProps> = (props) => {
    return (
        <>
            <Form.Label column className="optional">{localize("Details_Label_AccountId")}</Form.Label>
            <Col xs="8">
                <TypedSelect
                    options={props.offer.producer?.accounts.filter(x => x.canManage)}
                    showNullRow={!props.offer.producer?.accounts}
                    value={props.offer.customerAccount}
                    disabled={!props.allowPickingCustomer}
                    valueGetter={m => m.accountId}
                    textGetter={m => getProducerAccountDisplayName(m)}
                    onValueSelected={m => props.onOfferUpdated(OfferField.CustomerAccount, { customerAccount: m })} />
            </Col>
        </>
    );
};

const DeliveryMethodComponent: React.FC<AddOfferDetailsProps> = props => {

    const translators = useTranslators();

    const isFixFutures = props.offer.type === OfferTypeEnum.FixFuturesOnBasis;
    const lockAsLabel = props.offer.deliveryMethod != null &&
        (isFixFutures || props.isPricingDelayedPriceContractModal || props.lockDeliveryMethod);

    return <Form.Group as={Row} data-testid="delivery-method-row">
        <Form.Label column>{localize("Details_Label_DeliveryMethod")}</Form.Label>
        <Col xs="8">
            {lockAsLabel ?
                <div className="col-form-label">{translators.deliveryMethods.translate(props.offer.deliveryMethod)}</div>
                : <TypedSelect
                    value={props.offer.deliveryMethod}
                    options={props.deliveryMethods}
                    disabled={props.priceAcrossLocations}
                    showNullRow
                    required
                    className={props.isConvertBushelOnly && !props.offer.deliveryMethod ? 'bo-convert-required' : ''}
                    valueGetter={m => m}
                    textGetter={translators.deliveryMethods.translate}
                    onValueSelected={m => {
                        props.onOfferUpdated(OfferField.DeliveryMethod, { deliveryMethod: m });
                    }} />}
        </Col>
    </Form.Group>
}

interface CashPriceProps {
    value: number;
    isPricingType: boolean;
    isManualEntry: boolean;
    isMarketOrder: boolean;
    isDisabled: boolean;
    priceIncrement?: number;
    min: number;
    max: number;
    isPriceLocked?: boolean;
    onValueChanged(cashPrice: number): void;
    currencyCode: string;
    formState: OfferFormState;
    canMovePriceByTick: boolean;
}

const CashPrice: React.FC<CashPriceProps> = props => {

    if (props.isPricingType || props.isManualEntry)
        return <div className="col-form-label">{`${formatDecimalPlace(props.value)} ${props.currencyCode}`}</div>;

    if (props.isMarketOrder && !props.canMovePriceByTick) {
        return <div className="input-group">
            <input type="text" disabled value={`${formatDecimalPlace(props.value)} ${props.currencyCode}`} className="form-control" />
            <div className="input-group-append ml-1">
                <span className="hover-text"
                    title={localize("Details_Tooltip_CashPrice")}>
                    +/-
                </span>
            </div>
        </div>;
    }

    return <NumberEditor className={props.formState.hasUserUpdatedCashPrice ? 'form-control font-weight-bold' : 'form-control'}
        disabled={props.isDisabled}
        isInputLocked={props.isPriceLocked}
        required={!props.isMarketOrder}
        min={props.min}
        max={props.max}
        step={props.priceIncrement}
        allowDecimals
        precision={5}
        enforcePrecision
        roundingCent
        value={props.value}
        onValueChange={props.onValueChanged}
        unit={props.currencyCode}
    />
}

export default AddOfferDetails;
